import { useEffect, useRef, useState } from "react";
import { SPECIALTIES, INSURANCES } from "@/data/heallexa";

type Stat = {
  value: number;
  suffix: string;
  label: string;
  format?: "compact" | "int";
};

function formatCompact(n: number): { value: string; suffix: string } {
  if (n >= 1_000_000) return { value: (n / 1_000_000).toFixed(1).replace(/\.0$/, ""), suffix: "M+" };
  if (n >= 1_000) return { value: Math.floor(n / 1_000).toString(), suffix: "K+" };
  return { value: n.toString(), suffix: "" };
}

function AnimatedNumber({ value, suffix, format }: { value: number; suffix: string; format?: "compact" | "int" }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef<HTMLSpanElement | null>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    started.current = false;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const duration = 1400;
            const startTime = performance.now();
            const tick = (now: number) => {
              const t = Math.min(1, (now - startTime) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              setDisplay(value * eased);
              if (t < 1) requestAnimationFrame(tick);
              else setDisplay(value);
            };
            requestAnimationFrame(tick);
          }
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value]);

  const rendered = format === "compact"
    ? formatCompact(display).value
    : Math.round(display).toLocaleString();
  const renderedSuffix = format === "compact" ? formatCompact(value).suffix : suffix;

  return (
    <span ref={ref} className="tabular-nums">
      {rendered}
      <span className="text-primary-foreground/80">{renderedSuffix}</span>
    </span>
  );
}

// ponytail: static numbers matching the hero's SEO copy — swap for a stats
// endpoint if these ever need to be live.
const STATS: Stat[] = [
  { value: 6_300_000, suffix: "", label: "verified providers in our directory", format: "compact" },
  { value: 50, suffix: "", label: "U.S. states covered", format: "int" },
  { value: SPECIALTIES.length, suffix: "+", label: "medical specialties covered", format: "int" },
  { value: INSURANCES.length, suffix: "+", label: "insurance plans supported", format: "int" },
];

export function StatsBar() {
  return (
    <>
      <section className="relative z-10 bg-primary text-primary-foreground">
        <div className="px-6 py-5">
          <div className="mx-auto max-w-[1200px] grid grid-cols-2 gap-y-4 md:gap-y-0 md:grid-cols-4 md:divide-x md:divide-primary-foreground/20">
            {STATS.map((s) => (
              <div key={s.label} className="flex flex-col items-start gap-1 px-2 py-1 md:flex-row md:items-center md:gap-3 md:px-5 md:py-3">
                <div className="text-[clamp(1.25rem,1.8vw,1.625rem)] font-bold tracking-[-0.03em] text-primary-foreground leading-none">
                  <AnimatedNumber value={s.value} suffix={s.suffix} format={s.format} />
                </div>
                <p className="text-[12px] leading-snug text-primary-foreground/80 max-w-[160px]">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="border-y border-border bg-muted/20 py-5 px-6">
        <div className="mx-auto max-w-[900px] flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <p className="text-[12px] uppercase tracking-[0.15em] text-muted-foreground">Trusted platform</p>
            <p className="text-[14px] font-medium mt-0.5">Reviewed on Trustpilot</p>
          </div>
          
          <a href="https://www.trustpilot.com" target="_blank" rel="noopener noreferrer" className="text-[13px] text-muted-foreground hover:text-foreground">
            Read our reviews on Trustpilot
          </a>

          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <svg key={i} className="h-5 w-5" fill="#00B67A" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
              </svg>
            ))}
            <span className="text-[12px] text-muted-foreground ml-1 font-semibold">Trustpilot</span>
          </div>
        </div>
      </div>
    </>
  );
}
